// This MultiSelect component uses Downshift's useMultipleSelection + useSelect
// to provide a custom multiselect dropdown with removable tags and keyboard navigation.
import { useMultipleSelection, useSelect } from 'downshift';
import { combineRefs } from '@dist-marketplace-frontend/utils';
import { MultiSelectProps } from './MultiSelect.typed';
import { useCallback, useMemo, useRef } from 'react';
import ActionList from '../ActionList/ActionList';
import Dropdown from '../Dropdown/Dropdown';
import Icon from '../Icon/Icon';
import Tag from '../Tag/Tag';
import Text from '../Text/Text';
import * as Styled from './MultiSelect.styled';
import { useTheme } from 'styled-components';

// TODO: messages for error and information, error state, onBlur, etc
export const MultiSelect = <T,>(props: MultiSelectProps<T>) => {
  const {
    items: providedItems,
    selectedItems: providedSelectedItems,
    onChange,
    id,
    placeholder = 'Select one or more...',
    itemToString: providedItemToString,
    targetRef,
  } = props;
  const theme = useTheme();
  // A local ref for the target button - we'll combine this and a few other refs down below.
  // We need this one to focus on when the menu is opened since the others may be function refs
  // which don't provide access to the DOM element.
  const localTargetRef = useRef<HTMLButtonElement>(null);

  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection({
    initialSelectedItems: providedSelectedItems,
    selectedItems: providedSelectedItems,
    onSelectedItemsChange: ({ selectedItems }) => {
      onChange(selectedItems);
    },
  });

  // Handling this here so we don't need everyone to handle the null case.
  const itemToString = useCallback(
    (item: T | null) => {
      if (!item) return '';
      return providedItemToString(item);
    },
    [providedItemToString],
  );

  const itemsInMenu = useMemo(() => {
    // Use a Set of the item strings to avoid comparing references.
    const selectionSet = new Set(selectedItems.map(itemToString));
    return providedItems.filter(
      (item) => !selectionSet.has(itemToString(item)),
    );
  }, [selectedItems, providedItems, itemToString]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    openMenu,
  } = useSelect({
    selectedItem: null,
    defaultHighlightedIndex: 0,
    items: itemsInMenu,
    itemToString,
    stateReducer: (_state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          // For keyboard selection, keep highlighting at index 0
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: 0,
          };
        case useSelect.stateChangeTypes.ItemClick:
          // For mouse clicks, keep the menu open, but don't reset highlight since the mouse will
          // likely continue to be used near the now-selected item.
          return {
            ...changes,
            isOpen: true,
            // Don't change highlightedIndex on click to avoid interfering with mouse interaction
          };
      }
      return changes;
    },
    onStateChange: ({ type, selectedItem: newSelectedItem }) => {
      if (!newSelectedItem) return;

      switch (type) {
        case useSelect.stateChangeTypes.ItemClick:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          if (
            // Compare using itemToString to avoid comparing references.
            !selectedItems.some(
              (item) => itemToString(item) === itemToString(newSelectedItem),
            )
          ) {
            addSelectedItem(newSelectedItem);
          }
          break;
        default:
          break;
      }
    },
  });

  return (
    <>
      {/* Always call getMenuProps, even if not rendering the menu, to avoid Downshift accessibility/test warnings */}
      {/* https://github.com/downshift-js/downshift/issues/1272 */}
      {!isOpen && <ul {...getMenuProps({ style: { display: 'none' } })} />}
      <Dropdown
        isOpen={isOpen}
        // Focus should always stay on the combobox trigger, even when the dropdown is open.
        disableFocusTrap
        renderAnchor={({ ref: dropdownTargetRef, ...dropdownAnchorProps }) => {
          const { ref: toggleButtonRef, ...toggleButtonProps } =
            getToggleButtonProps(
              getDropdownProps({
                ...dropdownAnchorProps,
                id,
              }),
            );

          const combinedRef = combineRefs(
            toggleButtonRef,
            targetRef,
            localTargetRef,
          );

          return (
            <Styled.Container
              aria-label={
                selectedItems.length > 0
                  ? `${selectedItems.length} items selected. ${placeholder}`
                  : placeholder
              }
              // We want the dropdown to point at the MultiSelect container, but focus will be on
              // the toggle button.
              ref={dropdownTargetRef}
              onClick={() => {
                // We don't need to worry about closing the menu - the Dropdown will handle that.
                if (!isOpen) {
                  openMenu();
                  localTargetRef.current?.focus();
                }
              }}
            >
              {selectedItems.map(function renderSelectedItem(
                selectedItemForRender,
                index,
              ) {
                const tagText = itemToString(selectedItemForRender);
                return (
                  <Tag
                    key={tagText}
                    // Render as <span> to avoid nested <button> issues (invalid HTML & causes React warnings),
                    // but apply role="button" and tabIndex=0 to preserve accessibility and keyboard support.
                    as="span"
                    role="button"
                    aria-label={`remove ${tagText}`}
                    {...getSelectedItemProps({
                      selectedItem: selectedItemForRender,
                      index,
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          removeSelectedItem(selectedItemForRender);
                        }
                      },
                    })}
                    trailing={<Icon name={'CloseOutline'} size={'2xs'} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(selectedItemForRender);
                    }}
                    weight={'bold'}
                  >
                    {tagText}
                  </Tag>
                );
              })}
              <Text size="m" color={theme.brand.secondary.base}>
                {placeholder}
              </Text>
              <Styled.ComboboxTarget
                type="button"
                {...toggleButtonProps}
                ref={combinedRef}
              >
                <Icon
                  name="ChevronDownOutline"
                  size="s"
                  color={theme.brand.primary.base}
                />
              </Styled.ComboboxTarget>
            </Styled.Container>
          );
        }}
      >
        {() => (
          <Styled.ListContainer>
            {/* TODO: the ActionList and the Dropdown both create a listbox element.
            Dropdown should not create one so it's more versatile. */}
            <ActionList {...getMenuProps()}>
              {itemsInMenu.map((item, index) => {
                const isActiveItem = highlightedIndex === index;
                return (
                  <ActionList.Item
                    key={itemToString(item)}
                    {...getItemProps({
                      item,
                      index,
                    })}
                    isSelected={isActiveItem}
                  >
                    {itemToString(item)}
                  </ActionList.Item>
                );
              })}
            </ActionList>
          </Styled.ListContainer>
        )}
      </Dropdown>
    </>
  );
};
